import { Box, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import type { JSX } from "react";
import { useParams } from "react-router";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import Loading from "../Loading";
import {
    editMutation,
    type UserEditMutation,
    type UserEditMutationVariables,
    type UserQuery,
    type UserQueryVariables,
    userQuery,
} from "./queries";
import UserForm, { type IFormValues } from "./UserForm";

function Edit(): JSX.Element {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [editResult, executeEdit] = useMutation<UserEditMutation, UserEditMutationVariables>(editMutation);
    const [result] = useQuery<UserQuery, UserQueryVariables>({
        query: userQuery,
        variables: {
            userId: Number.parseInt(userId!, 10),
        },
    });

    const handleSubmit = async (values: IFormValues) => {
        const result = await executeEdit({
            data: {
                id: Number.parseInt(userId!, 10),
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
            },
        });
        if (result.error) {
            notifications.show({
                title: "Failed to save user",
                message: result.error.message,
            });
        } else {
            const user = result.data!.userEdit;
            notifications.show({
                title: "User saved",
                message: `User ${user.firstName} ${user.lastName} updated`,
            });
            navigate("..");
        }
    };

    let main = null;
    if (result.fetching) {
        main = <Loading />;
    } else if (result.error) {
        main = <Box>Error: {result.error.message}</Box>;
    } else {
        const user = result.data?.user;
        if (!user) {
            main = <Box>User not found</Box>;
        } else {
            const initialValues = {
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName,
            };
            main = (
                <UserForm handleSubmit={handleSubmit} initialValues={initialValues} fetching={editResult.fetching} />
            );
        }
    }

    return (
        <>
            <Title order={2}>Edit user</Title>
            {main}
            <Link to="..">Back</Link>
        </>
    );
}

export default Edit;
